import i18n from '@/lib/translations/i18n';
import type { CustomFieldTypes } from './customField';

export interface JiraInstance {
  id: string;
  name: string;
  url: string;
}

export interface JiraProject {
  id: string;
  name: string;
}

export interface JiraIssueType {
  id: string;
  name: string;
  hierarchyLevel: number;
}

export interface JiraStatus {
  id: string;
  name: string;
}

export interface JiraTeamField {
  id: string;
  name: string;
}

export interface JiraEstimateField {
  id: string;
  name: string;
}

export interface JiraDateField {
  id: string;
  name: string;
}

export interface JiraFields {
  teamFields: JiraTeamField[];
  estimateFields: JiraEstimateField[];
  dateFields: JiraDateField[];
}

export interface JiraIssueTypesAndStatusesResponse {
  issueTypes: JiraIssueType[];
  statuses: JiraStatus[];
}

export interface JiraIssueTypesWithHierarchyResponse {
  id: string;
  name: string;
  hierarchyLevel: number;
}

export interface JiraProjectItemCountResponse {
  count: number;
}

export interface ValidateJqlRequest {
  jql: string;
}

export interface ValidateJqlResponse {
  isValid: boolean;
  errors?: string[];
}

export interface JiraOauthRequestResponse {
  authorizationUri: string;
}

export interface JiraOauthResponseResponse {
  oauthCredentialsId: number;
  returnUrl: string;
}

export interface WorkIntegrationSyncRequest {
  syncType: SyncType;
}

export const SyncType = {
  PULL: 'PULL',
  PUSH: 'PUSH',
} as const;

export type SyncType = (typeof SyncType)[keyof typeof SyncType];

export const WorkIntegration_Keys = {
  JIRA: 'JIRA',
} as const;

export type WorkIntegration_Keys = (typeof WorkIntegration_Keys)[keyof typeof WorkIntegration_Keys];

export const ExternalWorkIntegrationStatusValues = {
  CREATED: 'CREATED',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  PARTIAL_FAILURE: 'PARTIAL_FAILURE',
} as const;

export type ExternalWorkIntegrationStatusValues =
  (typeof ExternalWorkIntegrationStatusValues)[keyof typeof ExternalWorkIntegrationStatusValues];

export const SynchronizationStatusValues = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
  DELETING: 'DELETING',
} as const;

export type SynchronizationStatusValues =
  (typeof SynchronizationStatusValues)[keyof typeof SynchronizationStatusValues];

export const FILTER_MODE_VALUES = ['BASIC', 'JQL'] as const;

export type FilterMode = (typeof FILTER_MODE_VALUES)[number];

export const FilterMode = {
  BASIC: 'BASIC',
  JQL: 'JQL',
} as const satisfies Record<FilterMode, FilterMode>;

export interface WorkIntegrationFilters {
  typeIds?: string[];
  statusIds?: string[];
  includeWorkAfter?: string;
  jql?: string;
  customFieldFilters?: JiraCustomFieldFilter[];
  filterMode?: FilterMode;
}

export const SYNC_DIRECTION_VALUES = ['PULL_ONLY', 'PUSH_ONLY', 'BIDIRECTIONAL'] as const;

export type SyncDirection = (typeof SYNC_DIRECTION_VALUES)[number];

const [PULL_ONLY, PUSH_ONLY, BIDIRECTIONAL] = SYNC_DIRECTION_VALUES;
export const SyncDirection = {
  PULL_ONLY,
  PUSH_ONLY,
  BIDIRECTIONAL,
} as const;

export interface IntegrationCustomFieldMapping {
  id: number;
  instanceFieldMappingId: number;
  syncDirection: SyncDirection;
}

export interface WorkIntegrationResponse {
  id: number;
  organizationId: string;
  credentialsId?: number;
  projectId: string;
  instanceId: string;
  instanceUrl: string;
  name?: string;
  sourceSystemType: WorkIntegration_Keys;
  status: ExternalWorkIntegrationStatusValues;
  synchronizationStatus: SynchronizationStatusValues;
  loopId: number;

  fieldMapping?: {
    estimateFieldId?: string | null;
    teamFieldId?: string | null;
    expectedStartDateFieldId?: string | null;
    expectedFinishDateFieldId?: string | null;
    plannedStartDateFieldId?: string | null;
    plannedFinishDateFieldId?: string | null;
  };
  filters: WorkIntegrationFilters;
  customFieldMappings?: IntegrationCustomFieldMapping[];
  lastPullSyncAt: string | null;
  lastPushSyncAt: string | null;
  lastPulledBy: string | null;
  lastPushedBy: string | null;
  isLastPullFullSync?: boolean | null;
  isLastPushFullSync?: boolean | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface JiraItemLevelMapping {
  id: number;
  organizationId: string;
  itemLevel: number;
  jiraLevel: number;
  instanceId: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ItemLevelMapping {
  itemLevel: number;
  jiraLevel: number;
}

export interface JiraItemTypeMapping {
  id: number;
  organizationId: string;
  itemTypeId: number;
  issueTypeId: number;
  instanceId: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface IssueTypeMapping {
  itemTypeId: number;
  issueTypeId: string;
}

export const WorkIntegrationSyncErrorType = {
  PARTIAL_PROCESSING: 'PARTIAL_PROCESSING',
  INVALID_CUSTOM_FIELD_FILTER: 'INVALID_CUSTOM_FIELD_FILTER',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
} as const;

export type WorkIntegrationSyncErrorType =
  (typeof WorkIntegrationSyncErrorType)[keyof typeof WorkIntegrationSyncErrorType];

export interface WorkIntegrationSyncErrorResponse {
  type: WorkIntegrationSyncErrorType;
  tooltip?: string | null;
  translationData?: Record<string, string> | null;
}

export const JIRA_FIELD_TYPE_VALUES = [
  'SELECT',
  'MULTI_SELECT',
  'DATE',
  'DATETIME',
  'TEXT',
  'MULTILINE_TEXT',
  'NUMERIC',
  'USER',
  'MULTI_USER',
  'TEAM',
  'VERSION',
  'COMPONENT',
  'LABELS',
  'CHECKBOX',
  'MULTI_CHECKBOX',
] as const;
export type JiraFieldType = (typeof JIRA_FIELD_TYPE_VALUES)[number];

export const JIRA_JQL_OPERATOR_VALUES = [
  'EQUALS',
  'NOT_EQUALS',
  'CONTAINS',
  'NOT_CONTAINS',
  'IN',
  'NOT_IN',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
  'GREATER_THAN',
  'GREATER_THAN_OR_EQUALS',
  'LESS_THAN',
  'LESS_THAN_OR_EQUALS',
  'BETWEEN',
  'NOT_BETWEEN',
] as const;
export type JiraJqlOperator = (typeof JIRA_JQL_OPERATOR_VALUES)[number];

export const OPERATOR_LABEL_KEYS = {
  EQUALS: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.equals),
  NOT_EQUALS: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.doesNotEqual),
  CONTAINS: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.contains),
  NOT_CONTAINS: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.doesNotContain),
  IN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.isOneOf),
  NOT_IN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.isNotOneOf),
  IS_EMPTY: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.isEmpty),
  IS_NOT_EMPTY: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.isNotEmpty),
  GREATER_THAN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.greaterThan),
  GREATER_THAN_OR_EQUALS: i18n.t(
    ($) => $.common.settings['work-integrations'].jira.operators.greaterThanOrEquals,
  ),
  LESS_THAN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.lessThan),
  LESS_THAN_OR_EQUALS: i18n.t(
    ($) => $.common.settings['work-integrations'].jira.operators.lessThanOrEquals,
  ),
  BETWEEN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.between),
  NOT_BETWEEN: i18n.t(($) => $.common.settings['work-integrations'].jira.operators.notBetween),
} as const;

const STANDARD_VALUE_OPERATORS = [
  'EQUALS',
  'NOT_EQUALS',
  'IN',
  'NOT_IN',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
] as const satisfies readonly JiraJqlOperator[];

const MULTI_VALUE_OPERATORS = [
  'IN',
  'NOT_IN',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
] as const satisfies readonly JiraJqlOperator[];

const TEXT_OPERATORS = [
  'CONTAINS',
  'NOT_CONTAINS',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
] as const satisfies readonly JiraJqlOperator[];

export const OPERATORS_BY_FIELD_TYPE: Record<JiraFieldType, readonly JiraJqlOperator[]> = {
  SELECT: STANDARD_VALUE_OPERATORS,
  MULTI_SELECT: MULTI_VALUE_OPERATORS,
  DATE: [
    'EQUALS',
    'NOT_EQUALS',
    'GREATER_THAN',
    'GREATER_THAN_OR_EQUALS',
    'LESS_THAN',
    'LESS_THAN_OR_EQUALS',
    'BETWEEN',
    'NOT_BETWEEN',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  DATETIME: [
    'EQUALS',
    'NOT_EQUALS',
    'GREATER_THAN',
    'GREATER_THAN_OR_EQUALS',
    'LESS_THAN',
    'LESS_THAN_OR_EQUALS',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  TEXT: TEXT_OPERATORS,
  MULTILINE_TEXT: TEXT_OPERATORS,
  NUMERIC: [
    'EQUALS',
    'NOT_EQUALS',
    'GREATER_THAN',
    'GREATER_THAN_OR_EQUALS',
    'LESS_THAN',
    'LESS_THAN_OR_EQUALS',
    'IS_EMPTY',
    'IS_NOT_EMPTY',
  ],
  USER: STANDARD_VALUE_OPERATORS,
  MULTI_USER: MULTI_VALUE_OPERATORS,
  TEAM: STANDARD_VALUE_OPERATORS,
  VERSION: STANDARD_VALUE_OPERATORS,
  COMPONENT: STANDARD_VALUE_OPERATORS,
  LABELS: STANDARD_VALUE_OPERATORS,
  CHECKBOX: ['EQUALS', 'IS_EMPTY', 'IS_NOT_EMPTY'],
  MULTI_CHECKBOX: MULTI_VALUE_OPERATORS,
};

export interface JiraCustomFieldMetadata {
  id: string;
  name: string;
  fieldType: JiraFieldType;
  supportedOperators: JiraJqlOperator[];
  requiresOptions: boolean;
  isMultiValue: boolean;
}

export interface JiraFieldSuggestion {
  value: string;
  displayName: string;
}

export interface JiraFieldSuggestionsResponse {
  results: JiraFieldSuggestion[];
}

export interface JiraFieldSuggestionsParams {
  fieldName: string;
  fieldValue?: string;
}

export interface UserSuggestion {
  accountId: string;
  displayName: string;
  avatarUrl: string;
}

export interface UserSuggestionsResponse {
  results: UserSuggestion[];
}

export interface UserSuggestionsParams {
  credentialsId: number;
  instanceId: string;
  searchTerm: string;
}

export interface UsersBulkParams {
  credentialsId: number;
  instanceId: string;
  accountIds: string[];
}

export interface ProjectVersion {
  id: string;
  name: string;
  released: boolean;
  archived: boolean;
}

export interface ProjectComponent {
  id: string;
  name: string;
  description?: string;
}

export interface ProjectVersionsParams {
  instanceId: string;
  projectKey: string;
  includeArchived?: boolean;
}

export interface ProjectComponentsParams {
  instanceId: string;
  projectKey: string;
}

export interface JiraCustomFieldFilter {
  id?: string;
  fieldId?: string;
  fieldType?: JiraFieldType;
  operator?: JiraJqlOperator;
  value?: string | number | string[] | null;
}

export interface FilterValidationErrors {
  field?: string;
  operator?: string;
  value?: string;
}

export interface CustomFieldFilter {
  fieldId: string;
  fieldType: JiraFieldType;
  operator: JiraJqlOperator;
  value?: string | string[] | number | null;
}

export interface JqlValidationResult {
  isValid: boolean;
  errors?: string[];
}

export interface FilterValidationError {
  filterIndex: number;
  property: string;
  message: string;
}

export interface ValidateCustomFieldFiltersResponse {
  isValid: boolean;
  generatedJql?: string;
  jqlValidation?: JqlValidationResult;
  filterErrors?: FilterValidationError[];
}

export interface WorkIntegrationInstance {
  id: number;
  organizationId: string;
  instanceUrl: string;
  instanceId: string;
  sourceSystemType: WorkIntegration_Keys;
  instanceName: string;
  credentialsId: number;
  credentialsUpdatedAt: string;
  credentialsUpdatedBy: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export const OAuthCredentialAuthStatusValues = {
  VALID: 'VALID',
  AUTH_FAILED: 'AUTH_FAILED',
  UNKNOWN: 'UNKNOWN',
} as const;

export type OAuthCredentialAuthStatus =
  (typeof OAuthCredentialAuthStatusValues)[keyof typeof OAuthCredentialAuthStatusValues];

export interface OAuthCredentialStatus {
  oauthCredentialsId: number;
  status: OAuthCredentialAuthStatus;
}

export interface OAuthCredentialStatusResponse {
  statuses: OAuthCredentialStatus[];
}

export interface PendingItemsCountResponse {
  count: number;
}

export interface CreateWorkIntegrationInstanceRequest {
  instanceUrl: string;
  instanceId: string;
  sourceSystemType: WorkIntegration_Keys;
  instanceName: string;
  credentialsId: number;
}

export type InstanceJiraFieldType =
  | 'SELECT'
  | 'MULTI_SELECT'
  | 'DATE'
  | 'TEXT'
  | 'MULTILINE_TEXT'
  | 'NUMERIC'
  | 'CHECKBOX';

export interface InstanceFieldMapping {
  id: number;
  instanceId: number;
  organizationId: string;
  tempoFieldId: number;
  tempoFieldName: string;
  tempoFieldType: CustomFieldTypes;
  jiraFieldId: string | null;
  jiraFieldName: string;
  jiraFieldType: InstanceJiraFieldType;
  mapName: string;
  validProjectIds: string[] | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface CreateInstanceFieldMappingRequest {
  tempoFieldId: number;
  tempoFieldName: string;
  tempoFieldType: CustomFieldTypes;
  jiraFieldId: string | null;
  jiraFieldName: string;
  jiraFieldType: InstanceJiraFieldType;
  mapName: string;
}

export interface UpdateInstanceFieldMappingRequest {
  mapName: string;
}

export interface FieldMappingUsage {
  instanceFieldMappingId: number;
  integrationIds: number[];
}

export interface FieldValueMapping {
  id: number;
  instanceFieldMappingId: number;
  organizationId: string;
  jiraValue: string;
  jiraValueId: string | null;
  loopValue: string;
  loopValueId: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface LoopFieldValue {
  id: number;
  name: string;
  isMapped: boolean;
}

export interface JiraFieldValue extends JiraFieldSuggestion {
  isMapped: boolean;
}

export interface SaveFieldValueMappingItem {
  jiraValue: string;
  jiraValueId?: string | null;
  loopValue: string;
  loopValueId?: string | null;
}

export interface SaveFieldValueMappingsRequest {
  mappings: SaveFieldValueMappingItem[];
}

export const ItemSyncStatusView = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  FAILED: 'FAILED',
  SYNCED: 'SYNCED',
  NOT_LINKED: 'NOT_LINKED',
} as const;

export type ItemSyncStatusView = (typeof ItemSyncStatusView)[keyof typeof ItemSyncStatusView];

export interface ItemSyncStatusResponse {
  itemId: number;
  state: ItemSyncStatusView;
  errorMessage: string | null;
  integrationId?: number | null;
}
